import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useGoBack from "hooks/useGoBack";
import { createPost, updatePost, uploadImages, uploadVideos } from "api/community";
import { useSnackbar } from "contexts/SnackbarContext";
import useCommunityCategories, { DEFAULT_ACTIVITY_TYPE } from "hooks/useCommunityCategories";
import useUserStore from "@store/useUserStore";
import MediaViewer from "components/common/MediaViewer";
import MentionDropdown from "components/community/MentionDropdown";
import ProfileAvatar from "components/common/ProfileAvatar";
import SubHeader from "components/common/SubHeader";
import useMentionAutocomplete from "hooks/useMentionAutocomplete";
import { parseTokensToDisplay, SERVER_MAX_POST_CONTENT } from "utils/mentionEditor";
import "pages/community/community.scss";

const MAX_MEDIA = 10;
const MAX_VIDEOS = 3;
const MAX_IMAGE_SIZE = 30 * 1024 * 1024; // 30MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif", "image/bmp", "image/tiff", "image/avif"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/x-matroska", "video/webm"];
const IMAGE_EXTS = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|avif)$/i;
const VIDEO_EXTS = /\.(mp4|mov|avi|mkv|webm)$/i;

// iOS Safari/WebView에서 file.type이 빈 문자열일 수 있으므로 확장자로 fallback
const getFileMediaType = (file) => {
    if (IMAGE_TYPES.includes(file.type)) return "image";
    if (VIDEO_TYPES.includes(file.type)) return "video";
    const name = file.name || "";
    if (IMAGE_EXTS.test(name)) return "image";
    if (VIDEO_EXTS.test(name)) return "video";
    return null;
};

const isVideoUrl = (url) => /\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url);

const CommunityCreate = ({ editMode = false, postId = null, initialData = null }) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const { showError } = useSnackbar();
    const userInfo = useUserStore((state) => state.userInfo);

    // 비로그인 시 로그인 페이지로 리다이렉트
    useEffect(() => {
        if (!editMode && !userInfo) {
            navigate("/login", { replace: true });
        }
    }, [userInfo, editMode, navigate]);

    const { categories } = useCommunityCategories();

    // 폼 상태
    // 수정 모드에서 토큰 원문이 노출되지 않도록 표시 텍스트로 변환해 시작한다.
    const [content, setContent] = useState(
        () => parseTokensToDisplay(initialData?.content || "").text
    );
    const [postCategory, setPostCategory] = useState(initialData?.post_category || "");

    // @멘션 자동완성
    const contentRef = useRef(null);
    const contentMention = useMentionAutocomplete({
        value: content,
        setValue: setContent,
        textareaRef: contentRef,
        maxLength: 1000,
    });

    // 수정 모드: 마운트 시 1회, 기존 멘션 토큰을 엔티티로 복원한다.
    const { initFromRaw } = contentMention;
    const mentionInitedRef = useRef(false);
    useEffect(() => {
        if (mentionInitedRef.current) return;
        mentionInitedRef.current = true;
        if (initialData?.content) initFromRaw(initialData.content);
    }, [initialData?.content, initFromRaw]);

    // 새 글 작성 시 "자유" 카테고리 기본 선택
    useEffect(() => {
        if (!initialData?.post_category && categories.length > 0 && !postCategory) {
            const free = categories.find((c) => c.label === "자유");
            if (free) setPostCategory(free.value);
        }
    }, [categories, initialData?.post_category, postCategory]);

    // media: { url, type, isExisting }
    const [media, setMedia] = useState(() => {
        if (!initialData?.media_urls?.length) return [];
        return initialData.media_urls.map((url) => ({
            url,
            type: isVideoUrl(url) ? "video" : "image",
            isExisting: true,
        }));
    });

    // 제출 중 상태
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 미디어 업로드 중 상태
    const [isUploading, setIsUploading] = useState(false);
    const [uploadState, setUploadState] = useState({ phase: '', progress: 0 });

    // 풀스크린 뷰어 상태
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    // 뒤로가기
    const goBack = useGoBack();
    const handleBackClick = useCallback(() => {
        goBack();
    }, [goBack]);

    // textarea onFocus 시 키보드 뒤로 가려지지 않도록 스크롤
    const handleContentFocus = useCallback(() => {
        setTimeout(() => {
            const textarea = document.querySelector('.content-input');
            textarea?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
    }, []);

    // 미디어 업로드 버튼 클릭
    const handleUploadClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    // 미디어 선택 → 즉시 서버 업로드 → 서버 URL로 미리보기
    const handleMediaSelect = useCallback(
        async (e) => {
            const files = Array.from(e.target.files);
            if (files.length === 0) return;

            // 개수 제한: 초과 시 전체 차단
            const remainingSlots = MAX_MEDIA - media.length;
            if (files.length > remainingSlots) {
                showError(`미디어는 최대 ${MAX_MEDIA}개까지 추가할 수 있습니다.`);
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            const currentVideoCount = media.filter((m) => m.type === "video").length;
            const newVideoFiles = files.filter((f) => getFileMediaType(f) === "video");
            if (currentVideoCount + newVideoFiles.length > MAX_VIDEOS) {
                showError(`영상은 최대 ${MAX_VIDEOS}개까지 업로드 가능합니다.`);
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            // 지원하지 않는 형식: 전체 차단
            const unsupported = files.find((f) => !getFileMediaType(f));
            if (unsupported) {
                showError("지원하지 않는 파일 형식이 포함되어 있습니다.");
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            // 용량 초과: 해당 파일만 제외
            const validFiles = files.filter((file) => {
                const mediaType = getFileMediaType(file);
                if (mediaType === "video" && file.size > MAX_VIDEO_SIZE) {
                    showError("영상 1개당 최대 100MB까지 업로드 가능합니다.");
                    return false;
                }
                if (mediaType === "image" && file.size > MAX_IMAGE_SIZE) {
                    showError("이미지 1개당 최대 30MB까지 업로드 가능합니다.");
                    return false;
                }
                return true;
            });

            if (validFiles.length === 0) {
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            // 이미지/영상 분리
            const imageFiles = validFiles.filter((f) => getFileMediaType(f) === "image");
            const videoFiles = validFiles.filter((f) => getFileMediaType(f) === "video");

            setIsUploading(true);

            try {
                setUploadState({ phase: '미디어 업로드 중', progress: 0 });

                // 이미지/영상 병렬 업로드
                const handleProgress = (e) => {
                    if (e.total) {
                        setUploadState({ phase: '미디어 업로드 중', progress: Math.round((e.loaded / e.total) * 100) });
                    }
                };

                const promises = [];
                if (imageFiles.length > 0) {
                    promises.push(
                        uploadImages(imageFiles, { onUploadProgress: handleProgress })
                            .then((result) => {
                                return result.data.map((item) => ({ url: item.url, type: "image" }));
                            })
                    );
                }
                if (videoFiles.length > 0) {
                    promises.push(
                        uploadVideos(videoFiles, { onUploadProgress: handleProgress })
                            .then((result) => {
                                return result.data.map((item) => ({ url: item.url, type: "video", thumbnailUrl: item.thumbnail_url || null }));
                            })
                    );
                }

                const results = await Promise.all(promises);
                const newMedia = results.flat();

                if (newMedia.length > 0) {
                    setMedia((prev) => [...prev, ...newMedia]);
                }
            } catch (error) {
                console.error("[업로드] 실패:", error?.response?.status, error?.response?.data || error.message);
                showError("미디어 업로드에 실패했습니다. 다시 시도해주세요.");
            } finally {
                setIsUploading(false);
                setUploadState({ phase: '', progress: 0 });
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        },
        [media, showError]
    );

    // 미디어 삭제
    const handleRemoveMedia = useCallback((index) => {
        setMedia((prev) => prev.filter((_, i) => i !== index));
    }, []);

    // 게시글 저장 (미디어는 이미 업로드됨, URL만 전달)
    const { serialize: serializeContent } = contentMention;
    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;

        if (!content.trim()) {
            showError("내용을 입력해주세요.");
            return;
        }

        // 표시 텍스트를 저장 토큰으로 직렬화. 서버는 토큰 포함 길이 기준으로 제한한다.
        const serialized = serializeContent().trim();
        if (serialized.length > SERVER_MAX_POST_CONTENT) {
            showError("멘션이 많아 글자 수 제한(1,000자)을 초과했어요.");
            return;
        }

        setIsSubmitting(true);

        try {
            const mediaUrls = media.map((m) => m.url);
            // 미디어가 없으면 media_type을 명시적으로 null로 비운다.
            // 영상 글을 텍스트 전용으로 수정할 때 기존 "video" 타입이 남으면
            // HotPostCarousel 등 media_type으로 분기하는 화면에서 빈 영상 슬롯이 노출된다.
            let mediaType = null;
            if (media.some((m) => m.type === "video")) {
                mediaType = "video";
            } else if (media.length > 0) {
                mediaType = "image";
            }

            if (editMode) {
                await updatePost(postId, {
                    content: serialized,
                    mediaUrls,
                    mediaType,
                    activityType: DEFAULT_ACTIVITY_TYPE,
                    postCategory: postCategory || undefined,
                });
                navigate(`/community/${postId}`, { replace: true });
            } else {
                await createPost(serialized, {
                    mediaUrls,
                    mediaType,
                    activityType: DEFAULT_ACTIVITY_TYPE,
                    postCategory: postCategory || undefined,
                });

                // 열려있는 커뮤니티 목록 탭에 갱신 신호 전송
                try {
                    const channel = new BroadcastChannel("community-events");
                    channel.postMessage({ type: "post-created" });
                    channel.close();
                } catch (_) {
                    // BroadcastChannel 미지원 환경 무시
                }

                // 새 탭이면 window.close(), 히스토리 있으면 navigate(-1)
                goBack();
            }
        } catch (error) {
            console.error("게시글 저장 실패:", error);
            showError("게시글 저장에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    }, [content, navigate, goBack, isSubmitting, showError, media, postCategory, editMode, postId, serializeContent]);

    // 제출 버튼 활성화 여부
    const isSubmitEnabled = content.trim().length > 0 && !isSubmitting && !isUploading;

    const currentUser = userInfo;
    const nickname = currentUser?.nickname || "나";

    return (
        <div className="community-page community-create">
            {/* 헤더: < | 새 게시글 | 작성 (공통 SubHeader) */}
            <SubHeader
                title={editMode ? "게시글 수정" : "새 게시글"}
                customGoback={handleBackClick}
                actionButton={{
                    label: isSubmitting
                        ? (editMode ? "수정 중..." : "작성 중...")
                        : (editMode ? "수정" : "작성"),
                    onClick: handleSubmit,
                    disabled: !isSubmitEnabled,
                }}
            />

            {/* 카테고리 선택 */}
            <div className="create-section">
                <label className="section-label">카테고리 선택</label>
                <div className="activity-type-list">
                    {categories.map((cat) => (
                        <button
                            key={cat.value}
                            className={`activity-type-chip${postCategory === cat.value ? " active" : ""}`}
                            onClick={() => {
                                setPostCategory((prev) =>
                                    prev === cat.value ? "" : cat.value
                                );
                            }}
                            type="button"
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* 구분선 */}
            <div className="section-divider" />

            {/* 미디어 추가 */}
            <div className="create-section">
                <div className="section-label-row">
                    <label className="section-label">미디어 추가</label>
                    <span className="media-counter">{media.length}/{MAX_MEDIA}</span>
                </div>
                <div className="image-scroll-list">
                    {media.length < MAX_MEDIA && !isUploading && (
                        <div
                            className="upload-button"
                            onClick={handleUploadClick}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    handleUploadClick();
                                }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label="미디어 추가"
                        >
                            <span className="upload-icon">+</span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleMediaSelect}
                            />
                        </div>
                    )}
                    {isUploading && (
                        <div className="upload-button uploading">
                            <div className="upload-spinner-small" />
                        </div>
                    )}
                    {media.map((item, index) => (
                        <div
                            key={item.url}
                            className="image-preview-item"
                            onClick={() => {
                                setViewerIndex(index);
                                setViewerOpen(true);
                            }}
                        >
                            {item.type === "video" ? (
                                <>
                                    {item.thumbnailUrl ? (
                                        <img src={item.thumbnailUrl} alt="영상 썸네일" />
                                    ) : (
                                        <video
                                            src={`${item.url}#t=0.1`}
                                            muted
                                            playsInline
                                            preload="metadata"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    )}
                                    <div className="video-indicator">
                                        <span>&#9654;</span>
                                    </div>
                                </>
                            ) : (
                                <img
                                    src={item.url}
                                    alt={`업로드 이미지 ${index + 1}`}
                                />
                            )}
                            <button
                                className="remove-image"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveMedia(index);
                                }}
                                aria-label={`미디어 ${index + 1} 삭제`}
                                type="button"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
                <ul className="media-guide">
                    <li>사진·영상 합쳐 최대 {MAX_MEDIA}개, 영상은 최대 {MAX_VIDEOS}개까지 올릴 수 있어요.</li>
                    <li>사진은 1장당 {MAX_IMAGE_SIZE / 1024 / 1024}MB, 영상은 1개당 {MAX_VIDEO_SIZE / 1024 / 1024}MB 이하만 가능해요.</li>
                </ul>
            </div>

            {/* 구분선 */}
            <div className="section-divider" />

            {/* 프로필 + 텍스트 입력 */}
            <div className="create-section create-section--content">
                <div className="profile-row">
                    <ProfileAvatar
                        imageUrl={currentUser?.profile_image_url}
                        size={36}
                        className="profile-avatar"
                    />
                    <span className="profile-nickname">{nickname}</span>
                </div>
                <textarea
                    ref={contentRef}
                    className="content-input"
                    placeholder="함께 나누고 싶은 경험, 질문, 정보를 자유롭게 작성해보세요."
                    value={content}
                    onChange={contentMention.handleChange}
                    onKeyDown={contentMention.handleKeyDown}
                    onFocus={handleContentFocus}
                    onBlur={contentMention.handleBlur}
                    aria-label="게시글 내용"
                    role="combobox"
                    aria-expanded={contentMention.open}
                    aria-controls={contentMention.listId}
                    aria-autocomplete="list"
                    aria-activedescendant={contentMention.open ? contentMention.activeOptionId : undefined}
                    maxLength={1000}
                />
                <MentionDropdown
                    id={contentMention.listId}
                    open={contentMention.open}
                    results={contentMention.results}
                    loading={contentMention.loading}
                    activeIndex={contentMention.activeIndex}
                    onSelect={contentMention.selectUser}
                    placement="docked"
                />
                <span className={`char-counter${content.length >= 900 ? ' near-limit' : ''}`}>
                    {content.length}/1,000
                </span>
            </div>

            {/* 하단 고정 작성하기 버튼 */}
            <div className="create-bottom-bar">
                <div className="submit-bar-inner">
                    <button
                        className="submit-button"
                        onClick={handleSubmit}
                        disabled={!isSubmitEnabled}
                        type="button"
                    >
                        {isSubmitting ? (editMode ? "수정 중..." : "작성 중...") : (editMode ? "수정하기" : "작성하기")}
                    </button>
                </div>
            </div>

            {/* 업로드 프로그레스 오버레이 */}
            {(isUploading || isSubmitting) && uploadState.phase && (
                <div className="upload-progress-overlay" role="status" aria-label="업로드 중">
                    <div className="upload-progress-content">
                        <div className="upload-spinner">
                            <div className="spinner-ring" />
                            <div className="spinner-ring" />
                            <div className="spinner-ring" />
                        </div>
                        <p className="upload-progress-text">
                            {uploadState.phase} {uploadState.progress}%
                        </p>
                    </div>
                </div>
            )}

            {/* 풀스크린 미디어 뷰어 */}
            {viewerOpen && (
                <MediaViewer
                    media={media.map((item) => ({ url: item.url, type: item.type }))}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerOpen(false)}
                />
            )}
        </div>
    );
};

export default CommunityCreate;
